import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Upload, Trash2, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface FundEntry {
  id?: string;
  fund_name: string;
  mtd_return: string;
  performance_type: string;
  portfolio_range: string;
  period: string;
  rank: number;
}

const TABS = [
  { label: "P Month to Date", range: "P", period: "1M" },
  { label: "P Quarter to Date", range: "P", period: "3M" },
  { label: "P Year to Date", range: "P", period: "YTD" },
  { label: "R Month to Date", range: "R", period: "1M" },
  { label: "R Quarter to Date", range: "R", period: "3M" },
  { label: "R Year to Date", range: "R", period: "YTD" },
];

const FundPerformanceEditor = () => {
  const { toast } = useToast();
  const [entries, setEntries] = useState<FundEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentTab = TABS[activeTab];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const { data, error } = await supabase
        .from('presentation_fund_performance')
        .select('*')
        .order('portfolio_range')
        .order('period')
        .order('performance_type')
        .order('rank');

      if (error) throw error;
      setEntries((data || []).map(d => ({ ...d, period: d.period || '1M' })));
    } catch (error) {
      console.error('Error loading fund performance:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentEntries = (type: string) =>
    entries.filter(
      e => e.portfolio_range === currentTab.range && e.period === currentTab.period && e.performance_type === type
    );

  const parseCsv = (text: string): { fund_name: string; mtd_return: string }[] => {
    const lines = text.trim().split('\n');
    const results: { fund_name: string; mtd_return: string }[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Skip metadata/header lines (FE Analytics format)
      const lower = line.toLowerCase();
      if (lower.includes('performance table')) continue;
      if (lower.includes('pricing spread')) continue;
      if (lower.includes('price range')) continue;
      if (lower.includes('currency:')) continue;
      if (lower === '"name","custom period performance"') continue;
      if (lower.includes('"name"')) continue;

      // Parse CSV with quoted fields: "Fund Name",1.23
      const match = line.match(/^"([^"]+)"[,\t]+(-?[\d.]+)$/);
      if (!match) continue;

      const fundName = match[1].trim();
      const returnVal = parseFloat(match[2]);

      if (!fundName || isNaN(returnVal)) continue;
      // Skip cash line
      if (fundName.toLowerCase().includes('pounds sterling')) continue;

      // Deduplicate (same fund appears multiple times in FE exports)
      if (seen.has(fundName)) continue;
      seen.add(fundName);

      results.push({ fund_name: fundName, mtd_return: returnVal.toFixed(2) + '%' });
    }

    return results;
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const text = await file.text();
    const parsed = parseCsv(text);

    if (parsed.length === 0) {
      toast({ title: "Error", description: "No valid rows found in CSV. Expected: Fund Name, Return", variant: "destructive" });
      return;
    }

    // Sort by return value descending
    const sorted = [...parsed].sort((a, b) => {
      const aVal = parseFloat(a.mtd_return.replace('%', ''));
      const bVal = parseFloat(b.mtd_return.replace('%', ''));
      return bVal - aVal;
    });

    const topN = Math.min(5, Math.floor(sorted.length / 2));
    const best = sorted.slice(0, topN);
    const worst = sorted.slice(-topN).reverse();

    // Remove existing entries for this range/period
    const remaining = entries.filter(
      e => !(e.portfolio_range === currentTab.range && e.period === currentTab.period)
    );

    const newEntries: FundEntry[] = [
      ...best.map((f, i) => ({
        fund_name: f.fund_name,
        mtd_return: f.mtd_return,
        performance_type: 'best',
        portfolio_range: currentTab.range,
        period: currentTab.period,
        rank: i + 1,
      })),
      ...worst.map((f, i) => ({
        fund_name: f.fund_name,
        mtd_return: f.mtd_return,
        performance_type: 'worst',
        portfolio_range: currentTab.range,
        period: currentTab.period,
        rank: i + 1,
      })),
    ];

    setEntries([...remaining, ...newEntries]);
    toast({ title: "CSV Parsed", description: `${parsed.length} funds loaded. Top ${topN} best & worst selected. Click Save to persist.` });

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeEntry = (type: string, rank: number) => {
    setEntries(entries.filter(
      e => !(e.portfolio_range === currentTab.range && e.period === currentTab.period && e.performance_type === type && e.rank === rank)
    ));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Delete existing for this range/period
      await supabase
        .from('presentation_fund_performance')
        .delete()
        .eq('portfolio_range', currentTab.range)
        .eq('period', currentTab.period);

      const toInsert = entries
        .filter(e => e.portfolio_range === currentTab.range && e.period === currentTab.period && e.fund_name.trim())
        .map(({ id, ...rest }) => rest);

      if (toInsert.length > 0) {
        const { error } = await supabase
          .from('presentation_fund_performance')
          .insert(toInsert);
        if (error) throw error;
      }

      toast({ title: "Saved", description: `${currentTab.label} fund performance data saved.` });
      await loadData();
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: "Error", description: "Failed to save.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    setIsSaving(true);
    try {
      await supabase
        .from('presentation_fund_performance')
        .delete()
        .eq('portfolio_range', currentTab.range)
        .eq('period', currentTab.period);

      setEntries(entries.filter(
        e => !(e.portfolio_range === currentTab.range && e.period === currentTab.period)
      ));
      toast({ title: "Cleared", description: `${currentTab.label} data cleared.` });
    } catch (error) {
      console.error('Delete error:', error);
      toast({ title: "Error", description: "Failed to clear data.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const bestEntries = currentEntries('best');
  const worstEntries = currentEntries('worst');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fund Performance Editor</CardTitle>
        <CardDescription>
          Upload a CSV (Fund Name, Return) for each range/period. The system auto-sorts into best &amp; worst performing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tab selector */}
        <div className="flex gap-2 flex-wrap">
          {TABS.map((tab, i) => {
            const count = entries.filter(e => e.portfolio_range === tab.range && e.period === tab.period).length;
            return (
              <Button
                key={i}
                variant={activeTab === i ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTab(i)}
              >
                {tab.label}
                {count > 0 && <Badge variant="secondary" className="ml-2 text-xs">{count}</Badge>}
              </Button>
            );
          })}
        </div>

        {/* CSV Upload */}
        <div className="flex items-center gap-3 p-4 border rounded-lg border-dashed bg-muted/30">
          <Upload className="h-5 w-5 text-muted-foreground" />
          <div className="flex-1">
            <Label className="text-sm font-medium">Upload CSV for {currentTab.label}</Label>
            <p className="text-xs text-muted-foreground">FE Analytics export format: "Name","Custom Period Performance" — duplicates & cash lines are auto-removed</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            onChange={handleCsvUpload}
            className="hidden"
          />
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-1" /> Choose File
          </Button>
        </div>

        {/* Best Performing */}
        <div>
          <h4 className="text-sm font-semibold text-green-700 mb-2">Best Performing</h4>
          <FundTable entries={bestEntries} type="best" onRemove={removeEntry} />
        </div>

        {/* Worst Performing */}
        <div>
          <h4 className="text-sm font-semibold text-red-700 mb-2">Worst Performing</h4>
          <FundTable entries={worstEntries} type="worst" onRemove={removeEntry} />
        </div>

        <div className="flex gap-2">
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Save {currentTab.label}
          </Button>
          <Button variant="destructive" size="sm" onClick={handleDeleteAll} disabled={isSaving}>
            <Trash2 className="h-4 w-4 mr-1" /> Clear {currentTab.label}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const FundTable = ({ entries, type, onRemove }: { entries: FundEntry[]; type: string; onRemove: (type: string, rank: number) => void }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="w-12">#</TableHead>
        <TableHead>Fund Name</TableHead>
        <TableHead className="w-28">Return</TableHead>
        <TableHead className="w-12"></TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {entries.map((entry) => (
        <TableRow key={`${type}-${entry.rank}`}>
          <TableCell>{entry.rank}</TableCell>
          <TableCell>{entry.fund_name}</TableCell>
          <TableCell className={type === 'best' ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
            {entry.mtd_return}
          </TableCell>
          <TableCell>
            <Button variant="ghost" size="icon" onClick={() => onRemove(type, entry.rank)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </TableCell>
        </TableRow>
      ))}
      {entries.length === 0 && (
        <TableRow>
          <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
            No entries. Upload a CSV to populate.
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
);

export default FundPerformanceEditor;
